// hooks/useAircraft.ts
import { useState, useEffect } from "react";
import { MapEngine } from "@/client/map/core/MapEngine";
import type { AdsbTarget } from "@/client/map/types";
import type { SkopaTarget } from "@/client/map/types";

export function useAircraftSelection() {
  const [selectedTargets, setSelectedTargets] = useState<AdsbTarget[]>([]);
  const [allTargets, setAllTargets] = useState<Map<string, SkopaTarget>>(
    new Map(),
  );

  useEffect(() => {
    let interval: any;
    let cleanupBusListener: (() => void) | null = null;

    const syncSelection = () => {
      const manager = MapEngine.getInstance()?.getAircraftManager();
      if (manager) {
        const currentSelected = manager.getSelectedAircraft();
        setSelectedTargets(currentSelected);
        setAllTargets(new Map(manager.getAllTargets()));
      }
    };

    const attachSelectionListener = () => {
      const manager = MapEngine.getInstance()?.getAircraftManager();
      const eventBus = MapEngine.getInstance()?.getEventBus();

      if (manager && eventBus) {
        clearInterval(interval);

        // Run an immediate sync frame
        syncSelection();

        // Listen for internal explicit state triggers
        eventBus.on("aircraft:selected-aircraft-updated", syncSelection);
        // Also listen to raw batch data stream updates to keep values updated in real time!
        eventBus.on("data:aircraft-batch-updated", syncSelection);

        cleanupBusListener = () => {
          eventBus.off("aircraft:selected-aircraft-updated", syncSelection);
          eventBus.off("data:aircraft-batch-updated", syncSelection);
        };
      }
    };

    // Keep scanning until the engine manager mounts
    interval = setInterval(attachSelectionListener, 100);
    attachSelectionListener();

    return () => {
      clearInterval(interval);
      if (cleanupBusListener) cleanupBusListener();
    };
  }, []);

  const select = (trackId: string) => {
    MapEngine.getInstance()?.getAircraftManager()?.selectAircraft(trackId);
  };

  const deselect = (trackId: string) => {
    MapEngine.getInstance()?.getAircraftManager()?.deselectAircraft(trackId);
  };

  const toggle = (trackId: string) => {
    MapEngine.getInstance()
      ?.getAircraftManager()
      ?.getSelection()
      .toggleAircraft(trackId);
  };

  const isSelected = (trackId: string): boolean => {
    return (
      MapEngine.getInstance()?.getAircraftManager()?.isSelected(trackId) ??
      false
    );
  };

  return {
    selectedTargets,
    allTargets,
    select,
    deselect,
    toggle,
    isSelected,
  };
}
